import { setUser } from '@sentry/react';
import Axios, { AxiosInstance } from 'axios';
import { makeObservable, observable, runInAction } from 'mobx';
import UserInfo from '../Models/UserInfo';
import LiteracyModel from '../Models/LiteracyModel';
import toTitleCase from '../Helpers/titleCase';

/* eslint-disable */

const setIntersect = (a: any[], b: any[]) => a.filter(x => new Set(b).has(x));
const setDifference = (a: any[], b: any[]) => a.filter(x => !(new Set(b).has(x)));

export default class GlobalStore {
    constructor() {
        this.axios = Axios.create({
            withCredentials: true
        });

        makeObservable(this, {
            initialised: observable,
            userInfo: observable,
            literacyModel: observable,
            loggingIn: observable,
            getCapsAchieved: observable,
            getCapsExtended: observable,
            getLitsAchieved: observable,
            getLitsInProgress: observable,
            getCapsInProgress: observable,
            getCapsAchievedOrExtended: observable,
            hasError: observable
        });
    }

    readonly axios: AxiosInstance;

    initialised = false;

    hasError: boolean = false;

    userInfo?: UserInfo;

    literacyModel?: LiteracyModel[];

    error?: Error;

    loggingIn = false;

    // todo: modularize this; DRY
    getCapsAchieved = () => {
        var caps: string[] = [];
        this.literacyModel?.map(l => {
            l.capabilities.map(c => {
                if (c.value == 3) {
                    caps.push(c.name)
                }
            });
        });
        return caps.map(c => toTitleCase(c)).sort();
    }

    getCapsExtended = () => {
        var caps: string[] = [];
        this.literacyModel?.map(l => {
            l.capabilities.map(c => {
                if (c.value > 3) {
                    caps.push(c.name)
                }
            });
        });
        return caps.map(c => toTitleCase(c)).sort();
    }

    getCapsAchievedOrExtended = () => {
        return this.getCapsAchieved().concat(this.getCapsExtended());
    }

    getCapsInProgress = () => {
        var caps: string[] = [];
        this.literacyModel?.map(l => {
            l.capabilities.map(c => {
                if (c.value > 0 && c.value < 3) {
                    caps.push(c.name)
                }
            });
        });
        return caps.map(c => toTitleCase(c)).sort();
    }

    // this is now entirely dependent on the 'literacy achieved' label
    getLitsAchieved = () => {
        return this.literacyModel?.filter(l =>
            l.achieved
        ).map(l =>
            toTitleCase(l.name)
        ).sort() ?? [];
    }

    getLitsInProgress = () => {
        var lits: string[] = [];
        this.literacyModel?.map(l => {
            if (l.capabilities.some(c => c.value > 0 && c.value < 3)) {
                lits.push(l.name);
            }
        });
        // litsInProgress may overlap litsAchieved. Return set difference. i.e. B \ (A ∩ B)
        const A = this.getLitsAchieved();
        const B = lits.map(l => toTitleCase(l)).sort();
        return setDifference(B, setIntersect(A, B));
    }

    handleError = (error: Error) => runInAction(() => {
        this.error = error;
    });

    initialiseAsync = async () => {
        if (this.initialised) {
            return;
        }
        runInAction(() => {
            this.initialised = true;
        });
        runInAction(() => {
            this.loadUserInfo();
        });
    };

    loadUserInfo = () => {
        (async () => {
            // eslint-disable-next-line no-debugger
            const user = await this.axios.get<UserInfo>('/api/auth/user');
            runInAction(() => {
                this.userInfo = user.data;
            });
            setUser({
                id: user.data.studentId,
                username: `${user.data.firstName} ${user.data.lastName}`
            });
        })().catch(() => {
            window.location.href = `/api/auth/login?returnUrl=${window.location.href}`;
            runInAction(() => {
                this.loggingIn = true;
            });
        });

        (async () => {
            const lits = await this.axios.get<LiteracyModel[]>('/api/capabilities');
            runInAction(() => {
                this.literacyModel = lits.data;
            });
        })().catch(() => {
            this.hasError = true;
        });
    };
}
